import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Download, 
  Settings, 
  Star, 
  Tag, 
  Filter, 
  Grid, 
  List, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Loader,
  Package,
  Shield,
  Cpu,
  Code,
  Database,
  Brain,
  Microscope,
  Zap,
  Globe,
  Layers,
  Server,
  TrendingUp,
  Award,
  BookOpen,
  ExternalLink,
  Play,
  Pause
} from 'lucide-react';
import { mockSoftwareLibrary, Software } from '../../../mock/data';
import toast from 'react-hot-toast';

const AppCenter: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLicense, setSelectedLicense] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [software, setSoftware] = useState<Software[]>(mockSoftwareLibrary);
  const [isLoading, setIsLoading] = useState(false);
  const [operatingApp, setOperatingApp] = useState<string | null>(null);

  // 获取所有分类
  const categories = ['all', ...Array.from(new Set(software.map(s => s.category)))];
  const licenses = ['all', ...Array.from(new Set(software.map(s => s.license)))];
  const statuses = ['all', ...Array.from(new Set(software.map(s => s.status)))];

  // 过滤软件
  const filteredSoftware = software.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesLicense = selectedLicense === 'all' || item.license === selectedLicense;
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    
    return matchesSearch && matchesCategory && matchesLicense && matchesStatus;
  });

  // 获取分类图标
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'ai/ml':
        return Brain;
      case 'hpc':
        return Server;
      case 'bioinformatics':
        return Microscope;
      case 'chemistry':
        return Database;
      case 'physics':
        return Zap;
      case 'engineering':
        return Settings;
      case 'data science':
        return TrendingUp;
      default:
        return Package;
    }
  };

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'installing':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return Download;
      case 'installing':
        return CheckCircle;
      case 'pending_approval':
        return Clock;
      default:
        return Package;
    }
  };

  // 申请软件
  const handleApplySoftware = async (softwareId: string) => {
    setOperatingApp(softwareId);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSoftware(prev => prev.map(item => 
        item.id === softwareId 
          ? { ...item, status: 'pending_approval' }
          : item
      ));
      
      toast.success('软件申请已提交，请等待管理员审核');
    } catch (error) {
      toast.error('申请失败，请稍后重试');
    } finally {
      setOperatingApp(null);
    }
  };

  // 安装软件
  const handleInstallSoftware = async (softwareId: string) => {
    setOperatingApp(softwareId);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSoftware(prev => prev.map(item => 
        item.id === softwareId 
          ? { ...item, status: 'installing' as const }
          : item
      ));
      
      toast.success('软件安装成功');
    } catch (error) {
      toast.error('安装失败，请稍后重试');
    } finally {
      setOperatingApp(null);
    }
  };

  // 软件卡片组件
  const SoftwareCard = ({ app }: { app: Software }) => {
    const CategoryIcon = getCategoryIcon(app.category);
    const StatusIcon = getStatusIcon(app.status);
    const isOperating = operatingApp === app.id;

    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl portrait:rounded-xl border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
        <div className="p-6 portrait:p-4">
          {/* 头部 */}
          <div className="flex items-start justify-between mb-4 portrait:mb-3">
            <div className="flex items-start space-x-3 portrait:space-x-2">
              <div className="p-3 portrait:p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl portrait:rounded-lg text-white">
                <CategoryIcon className="w-6 h-6 portrait:w-5 portrait:h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold portrait:text-sm text-gray-900 mb-1 portrait:mb-0.5 group-hover:text-blue-600 transition-colors">
                  {app.name}
                </h3>
                <p className="text-sm portrait:text-xs text-gray-600 mb-2 portrait:mb-1">{app.version}</p>
                <div className="flex items-center space-x-2 portrait:space-x-1">
                  <span className={`inline-flex items-center space-x-1 portrait:space-x-0.5 px-2 py-1 portrait:px-1.5 portrait:py-0.5 rounded-full text-xs portrait:text-[10px] font-medium border ${getStatusStyle(app.status)}`}>
                    <StatusIcon className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                    <span>
                      {app.status === 'installing' ? '已安装' :
                       app.status === 'available' ? '可安装' :
                       app.status === 'pending_approval' ? '待审核' : '未知状态'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-1 portrait:space-x-0.5">
              <Star className="w-4 h-4 portrait:w-3.5 portrait:h-3.5 text-yellow-500" />
              <span className="text-sm portrait:text-xs font-medium text-gray-700">4.5</span>
            </div>
          </div>

          {/* 描述 */}
          <p className="text-sm portrait:text-xs text-gray-600 mb-4 portrait:mb-3 line-clamp-2">
            {app.description}
          </p>

          {/* 标签 */}
          <div className="flex flex-wrap gap-2 portrait:gap-1 mb-4 portrait:mb-3">
            {app.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 portrait:px-1.5 portrait:py-0.5 bg-gray-100 text-gray-700 text-xs portrait:text-[10px] rounded-lg portrait:rounded-md"
              >
                {tag}
              </span>
            ))}
            {app.tags.length > 3 && (
              <span className="px-2 py-1 portrait:px-1.5 portrait:py-0.5 bg-gray-100 text-gray-700 text-xs portrait:text-[10px] rounded-lg portrait:rounded-md">
                +{app.tags.length - 3}
              </span>
            )}
          </div>

          {/* 底部信息 */}
          <div className="flex items-center justify-between pt-4 portrait:pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-4 portrait:space-x-2 text-xs portrait:text-[10px] text-gray-600">
              <div className="flex items-center space-x-1 portrait:space-x-0.5">
                <Shield className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                <span>{app.license}</span>
              </div>
              <div className="flex items-center space-x-1 portrait:space-x-0.5">
                <Tag className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                <span className="hidden sm:inline">{app.category}</span>
                <span className="sm:hidden">{app.category.split(' ')[0]}</span>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center space-x-2 portrait:space-x-1">
              {app.status === 'available' && (
                <button
                  onClick={() => handleInstallSoftware(app.id)}
                  disabled={isOperating}
                  className="flex items-center space-x-1 portrait:space-x-0.5 px-3 py-1.5 portrait:px-2 portrait:py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs portrait:text-[10px] rounded-lg portrait:rounded-md transition-all duration-200 disabled:opacity-50"
                >
                  {isOperating ? (
                    <>
                      <Loader className="w-3 h-3 portrait:w-2.5 portrait:h-2.5 animate-spin" />
                      <span className="hidden sm:inline">安装中</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                      <span>安装</span>
                    </>
                  )}
                </button>
              )}

              {app.status === 'installing' && (
                <button className="flex items-center space-x-1 portrait:space-x-0.5 px-3 py-1.5 portrait:px-2 portrait:py-1 bg-green-600 text-white text-xs portrait:text-[10px] rounded-lg portrait:rounded-md">
                  <CheckCircle className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                  <span>已安装</span>
                </button>
              )}

              {app.status === 'pending_approval' && (
                <button className="flex items-center space-x-1 portrait:space-x-0.5 px-3 py-1.5 portrait:px-2 portrait:py-1 bg-yellow-600 text-white text-xs portrait:text-[10px] rounded-lg portrait:rounded-md">
                  <Clock className="w-3 h-3 portrait:w-2.5 portrait:h-2.5" />
                  <span>待审核</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 统计数据
  const stats = {
    total: software.length,
    installed: software.filter(s => s.status === 'installing').length,
    available: software.filter(s => s.status === 'available').length,
    pending: software.filter(s => s.status === 'pending_approval').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 portrait:p-4">
      {/* 顶部标题区 */}
      <div className="max-w-7xl mx-auto mb-8 portrait:mb-5">
        <div className="flex items-center justify-between portrait:flex-col portrait:items-start portrait:space-y-4">
          <div className="flex items-center space-x-5 portrait:space-x-4">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 portrait:w-12 portrait:h-12 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Package className="w-7 h-7 portrait:w-6 portrait:h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 portrait:w-4 portrait:h-4 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                <Star className="w-2.5 h-2.5 portrait:w-2 portrait:h-2 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent leading-tight">
                应用中心
              </h1>
              <p className="text-gray-600 mt-2 portrait:mt-1 text-base portrait:text-sm leading-relaxed">发现和管理您的应用程序</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 portrait:space-x-2 portrait:w-full portrait:justify-start">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center space-x-2 portrait:space-x-1.5 px-4 py-2 portrait:px-3 portrait:py-1.5 bg-white/70 backdrop-blur-sm hover:bg-white border border-white/50 rounded-xl portrait:rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm portrait:text-xs"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4 portrait:w-3.5 portrait:h-3.5" /> : <Grid className="w-4 h-4 portrait:w-3.5 portrait:h-3.5" />}
              <span>{viewMode === 'grid' ? '列表' : '网格'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">总应用数</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Package className="w-6 h-6 text-blue-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">已安装</p>
                <p className="text-2xl font-bold">{stats.installed}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircle className="w-6 h-6 text-green-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">可安装</p>
                <p className="text-2xl font-bold">{stats.available}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Download className="w-6 h-6 text-purple-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100">待审核</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-yellow-200" />
              </div>
            </div>
          </div>
        </div>

        {/* 搜索和筛选区 */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* 搜索框 */}
            <div className="lg:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜索应用..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* 分类筛选 */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">所有分类</option>
                {categories.filter(c => c !== 'all').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* 许可证筛选 */}
            <div>
              <select
                value={selectedLicense}
                onChange={(e) => setSelectedLicense(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">所有许可证</option>
                {licenses.filter(l => l !== 'all').map(license => (
                  <option key={license} value={license}>{license}</option>
                ))}
              </select>
            </div>

            {/* 状态筛选 */}
            <div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">所有状态</option>
                <option value="installed">已安装</option>
                <option value="available">可安装</option>
                <option value="pending_approval">待审核</option>
                <option value="unavailable">不可用</option>
              </select>
            </div>
          </div>
        </div>

        {/* 应用列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">加载中...</span>
            </div>
          </div>
        ) : filteredSoftware.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Package className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无应用</h3>
            <p className="text-gray-600">没有找到符合条件的应用程序</p>
          </div>
        ) : (
          <div className={`${
            viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-0'
          }`}>
            {viewMode === 'list' ? (
              // 列表视图
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed min-w-[1300px]">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="w-[340px] px-6 py-4 text-left text-sm font-semibold text-gray-900">应用名称</th>
                        <th className="w-[120px] px-6 py-4 text-center text-sm font-semibold text-gray-900">分类</th>
                        <th className="w-[100px] px-6 py-4 text-center text-sm font-semibold text-gray-900">版本</th>
                        <th className="w-[120px] px-6 py-4 text-center text-sm font-semibold text-gray-900">状态</th>
                        <th className="w-[120px] px-6 py-4 text-center text-sm font-semibold text-gray-900">许可证</th>
                        <th className="w-[100px] px-6 py-4 text-center text-sm font-semibold text-gray-900">评分</th>
                        <th className="w-[160px] px-6 py-4 text-center text-sm font-semibold text-gray-900">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200/50">
                      {filteredSoftware.map(app => {
                        const CategoryIcon = getCategoryIcon(app.category);
                        const StatusIcon = getStatusIcon(app.status);
                        const isOperating = operatingApp === app.id;
                        
                        return (
                          <tr key={app.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg text-white flex-shrink-0">
                                  <CategoryIcon className="w-5 h-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-gray-900 truncate">{app.name}</div>
                                  <div className="text-sm text-gray-500 truncate">{app.description}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm text-gray-600 font-medium">{app.category}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm text-gray-600 font-medium">{app.version}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyle(app.status)}`}>
                                <StatusIcon className="w-3 h-3" />
                                <span>
                                  {app.status === 'installing' ? '已安装' :
                                   app.status === 'available' ? '可安装' :
                                   app.status === 'pending_approval' ? '待审核' : '未知状态'}
                                </span>
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="text-sm text-gray-600 font-medium">{app.license}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <Star className="w-4 h-4 text-yellow-500" />
                                <span className="text-sm font-medium text-gray-700">4.5</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-center space-x-2">
                                {app.status === 'available' ? (
                                  <button
                                    onClick={() => handleInstallSoftware(app.id)}
                                    disabled={isOperating}
                                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                  >
                                    {isOperating ? (
                                      <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        <span>安装中</span>
                                      </>
                                    ) : (
                                      <>
                                        <Download className="w-4 h-4" />
                                        <span>安装</span>
                                      </>
                                    )}
                                  </button>
                                ) : app.status === 'installing' ? (
                                  <button
                                    onClick={() => handleInstallSoftware(app.id)}
                                    disabled={isOperating}
                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    卸载
                                  </button>
                                ) : (
                                  <span className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                                    待审核
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              // 网格视图
              filteredSoftware.map(app => (
                <SoftwareCard key={app.id} app={app} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppCenter;
